import type { DocumentLoadPhase } from '@/components/editor-v3/engine/useSuperDocEngine'

import type { DocumentSeedStage } from '@/types/pursuit/Document'

interface DocumentLoadProgressProps {
  /** Which step of the open this is, in the user's terms. */
  label: string
  /**
   * How far along that step is, 0–1, when the step can say. A download can;
   * SuperDoc's layout pass cannot, and leaves this out.
   */
  progress?: number | null
  /** A quieter line under the label — "1.2 MB of 30.3 MB". */
  detail?: string | null
}

/**
 * The steps of an open, in the user's terms. Every surface that shows the
 * wait names its steps from here, so the wording is one wording.
 */
export const LOAD_LABELS = {
  connecting: 'Connecting…',
  preparing: 'Preparing document…',
  /** The bytes coming down to the browser. */
  downloading: 'Loading file…',
  opening: 'Opening document…',
  rendering: 'Laying out pages…',
} as const

const PHASE_LABELS: Record<DocumentLoadPhase, string> = LOAD_LABELS

/**
 * What the backend is doing with the file, in the user's terms rather than the
 * task's. The terminal stages are absent on purpose: by then the wait belongs
 * to something else — the seeded document travelling to this client, or the
 * client's own import — and `preparing` is the honest thing to say about it.
 */
const SEED_STAGE_LABELS: Partial<Record<DocumentSeedStage, string>> = {
  queued: LOAD_LABELS.preparing,
  downloading: 'Fetching file…',
  importing: 'Reading document…',
  storing_media: 'Saving images…',
  writing_room: 'Almost ready…',
}

/**
 * The label for a step of the editor's open. A backend import in progress
 * names the wait more precisely than `preparing` can.
 */
export const documentLoadLabel = (
  phase: DocumentLoadPhase,
  seedStage?: DocumentSeedStage,
): string =>
  (seedStage ? SEED_STAGE_LABELS[seedStage] : undefined) ?? PHASE_LABELS[phase]

/**
 * The wait before a document is on screen, for the editor and the viewer
 * alike. The caller owns the background.
 *
 * A step that can measure itself — a download — fills the bar. SuperDoc's
 * layout pass cannot: it lays a document out in a single synchronous pass that
 * reports nothing while it runs and cannot be broken up from outside — a
 * 69-page proposal spends about 1.7s in it — so there is no progress to read.
 * What that step can do instead is say that it is the one running, which the
 * caller paints before starting it.
 *
 * The sweep animates `transform` and nothing else, so the compositor can run it
 * without the main thread; a width or background-position animation would stop
 * dead the moment the layout pass begins.
 */
const DocumentLoadProgress: React.FC<DocumentLoadProgressProps> = ({
  label,
  progress,
  detail,
}) => {
  const percent =
    progress == null
      ? null
      : Math.round(Math.min(1, Math.max(0, progress)) * 100)
  return (
    <div
      className="flex h-full w-full flex-col items-center justify-center gap-3"
      role="status"
    >
      {/* The track has to read against the document canvas the wait is painted
          on, and that canvas is `primary-200` itself in the professional theme.
          A step past it at low opacity reads without competing with the chip. */}
      <div
        className="h-1 w-56 overflow-hidden rounded-full bg-primary-400/40 dark:bg-primary-800"
        {...(percent === null
          ? {}
          : {
              role: 'progressbar',
              'aria-label': label,
              'aria-valuemin': 0,
              'aria-valuemax': 100,
              'aria-valuenow': percent,
            })}
      >
        {percent === null ? (
          /* A plain div, not the SVG spinner this replaced: Chrome does not
             give SVG geometry its own compositing layer, so the spinner's
             rotation had to be driven by the very thread the layout pass was
             holding. The will-change asks for that layer up front.

             Reduced motion — which a Windows remote desktop reports for
             everyone working inside it — keeps the short bar and fades it in
             place. Filling the track instead left a solid accent bar with no
             track behind it, which reads as a load that has finished. */
          <div className="h-full w-1/4 rounded-full bg-accent-500 will-change-transform motion-safe:animate-wave motion-reduce:animate-pulse" />
        ) : (
          <div
            className="h-full rounded-full bg-accent-500 transition-[width] duration-150 ease-out"
            style={{ width: `${percent}%` }}
          />
        )}
      </div>
      <div className="flex flex-col items-center gap-1.5">
        <p className="text-sm text-primary-500">{label}</p>
        {/* Kept at its height when there is nothing to say, so a step that
            starts saying how far it has come does not push the bar up as it
            does — the bar stays where the step before it had it. */}
        <p className="min-h-4 text-xs tabular-nums text-primary-400">
          {detail}
        </p>
      </div>
    </div>
  )
}

export default DocumentLoadProgress
